<template>
    <div>
        <div class="bg-white border-bottom">
            <div class="container py-3">
                <div class="d-flex align-items-center">
                    <img
                        src="https://getasia.s3.ap-southeast-1.amazonaws.com/images/hextar_logo.png"
                        style="height: 48px; width: auto;"
                        alt="Hextar"
                    >
                    <div class="border-left ml-3 pl-3">
                        <h5 class="font-weight-bold mb-0">Panel Directory</h5>
                        <small class="text-muted">Find a panel by location and type</small>
                    </div>
                </div>
            </div>
        </div>
        <div class="py-3 bg-info">
            <div class="container">

                <div class="row">

                    <!-- Search -->
                    <div class="col-lg-6 mb-2 mb-lg-0">
                        <b-input-group>
                            <b-input-group-prepend>
                                <b-input-group-text class="bg-white border-0">
                                    <i class="mdi mdi-magnify"></i>
                                </b-input-group-text>
                            </b-input-group-prepend>
                            <b-form-input
                                v-model="filter"
                                class="border-0"
                                placeholder="Search panel..."
                                autocomplete="off"
                                v-on:change="searchListing"
                            />
                        </b-input-group>
                    </div>

                    <!-- Location -->
                    <div class="col-lg-3 mb-2 mb-lg-0">
                        <multiselect
                            v-model="locationFilter"
                            track-by="value"
                            label="text"
                            :placeholder="'All Locations'"
                            :select-label="'Select'"
                            :selected-label="'Selected'"
                            :deselect-label="'Select'"
                            :options="locationDdl"
                            :searchable="true"
                            :allow-empty="false"
                            @input="searchListing"
                        />
                    </div>

                    <!-- Panel Type -->
                    <div class="col-lg-3">
                        <multiselect
                            v-model="panelTypeFilter"
                            track-by="value"
                            label="text"
                            :placeholder="'All Panel Types'"
                            :select-label="'Select'"
                            :selected-label="'Selected'"
                            :deselect-label="'Select'"
                            :options="panelTypeDdl"
                            :searchable="true"
                            :allow-empty="false"
                            @input="searchListing"
                        />
                    </div>
                </div>
            </div>
        </div>
        
        <div class="bg-light">
            <div class="container py-4">
                <div v-if="loading || !status.list" class="d-flex justify-content-center m-5 p-5">
                    <b-spinner variant="info" role="status" class="m-5" />
                </div>
                <template v-else>
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <h5 class="font-weight-bold mb-0">Available Panels</h5>
                        <small class="text-muted">{{ $t('label.total') }}: {{ totalPanels }}</small>
                    </div>
                    <div class="row">
                        <div
                            v-for="item in tableRows"
                            :key="item.uuid"
                            class="col-md-6 mb-3 fade-in"
                        >
                            <div class="card shadow-sm h-100 mb-0 border-0">
                                <div class="card-body p-4 d-flex flex-column">

                                    <div class="mb-3">
                                        <b-badge variant="info" class="mb-2 px-2 py-1">
                                            {{ item.panelType?.name || '--' }}
                                        </b-badge>

                                        <h5 class="font-weight-bold mb-0 text-break">
                                            {{ item.name || '--' }}
                                        </h5>
                                    </div>

                                    <div class="mb-3">
                                        <p class="text-muted mb-2">
                                            <i class="mdi mdi-map-marker-outline text-info mr-2"></i>
                                            {{ item.location?.name || '--' }}
                                        </p>

                                        <p class="text-muted mb-2">
                                            <i class="mdi mdi-phone-outline text-info mr-2"></i>
                                            {{ item.phone ? `${item.phoneCode || ''} ${item.phone}`.trim() : '--' }}
                                        </p>

                                        <p class="text-muted text-break mb-2">
                                            <i class="mdi mdi-email-outline text-info mr-2"></i>
                                            {{ item.email || '--' }}
                                        </p>

                                        <p
                                            class="text-muted text-break mb-0"
                                            style="white-space: pre-line;"
                                        >
                                            <i class="mdi mdi-map-marker-radius-outline text-info mr-2"></i>
                                            {{ item.address || '--' }}
                                        </p>
                                    </div>

                                    <div class="mt-auto border-top pt-3 d-flex align-items-center justify-content-between">
                                        <!-- <b-button
                                            variant="info"
                                            size="sm"
                                            @click="openForm(item)"
                                        >
                                            View Details
                                            <i class="mdi mdi-arrow-right ml-1"></i>
                                        </b-button> -->

                                        <b-button
                                            v-if="item.mapLink"
                                            :href="item.mapLink"
                                            variant="info col-md-4"
                                            size="sm"
                                            class="ml-2"
                                        >
                                            <i class="mdi mdi-open-in-new"></i> Map
                                        </b-button>

                                        <b-button
                                            variant="info"
                                            size="sm"
                                            class="ml-2 col-md-4"
                                            :disabled="!item.phone || copiedPhones[item.uuid]"
                                            @click="copyPhone(item)"
                                        >
                                            <i :class="copiedPhones[item.uuid] ? 'mdi mdi-check' : 'mdi mdi-content-copy'"></i>
                                            {{ copiedPhones[item.uuid] ? 'Copied' : 'Copy Number' }}
                                        </b-button>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                    <b-alert v-if="tableRows.length == 0" show variant="warning" class="text-center">
                        <i class="mdi mdi-alert-circle-outline"></i> {{ $t('message.noResultsFound') }}
                    </b-alert>
                </template>
            </div>
        </div>
        <b-modal id="panel-details" title="Panel Details" hide-footer centered>
            <div v-if="!status.detail" class="d-flex justify-content-center p-5">
                <b-spinner variant="info" role="status" />
            </div>
            <div v-else-if="panelLoaded" class="p-2">
                <div class="bg-light border rounded p-3 mb-4">
                    <b-badge variant="info" class="mb-2">{{ data.panelType?.name || '--' }}</b-badge>
                    <h4 class="font-weight-bold mb-0 text-break">{{ data.name || '--' }}</h4>
                </div>
                <div class="mb-3">
                    <small class="text-muted d-block mb-1">Location</small>
                    <span>{{ data.location?.name || '--' }}</span>
                    <p class="text-muted small mt-1 mb-0">{{ data.address || '--' }}</p>
                </div>
                <div class="mb-3">
                    <small class="text-muted d-block mb-1">Phone</small>
                    <span>{{ data.phone ? `${data.phoneCode || ''} ${data.phone}`.trim() : '--' }}</span>
                </div>
                <div class="mb-4">
                    <small class="text-muted d-block mb-1">Email</small>
                    <span class="text-break">{{ data.email || '--' }}</span>
                </div>
                <div class="d-flex justify-content-end border-top pt-3">
                    <b-button variant="outline-secondary" @click="$bvModal.hide('panel-details')">Close</b-button>
                </div>
            </div>
            <b-alert v-else show variant="warning">Panel information is unavailable.</b-alert>
        </b-modal>
    </div>
</template>
<script>
import i18n from "@/i18n";
import { alertHelper } from "@/utils";
import Multiselect from "vue-multiselect";
import { mapState } from "vuex";
import { panelMethods, lookupMethods } from "@/state/public";
import { PANEL_MODULE } from "@/state/public/panel.state";

export default {
    page: {
        title: i18n.t("module.panel"),
    },
    components: { Multiselect },
    async created() {
        await this.lookupLocation();
        await this.lookupPanelType();
        await this.getPanelList();
        this.loading = false;
    },
    data() {
        return {
            loading: true,
            panelLoaded: false,
            copiedPhones: {},
            copyTimers: {},
            filter: "",
            locationFilter: { value: 1, text: i18n.t('label.all') },
            panelTypeFilter: { value: 1, text: i18n.t('label.all') },
            searchCriteria: {
                isActive: 1,
                location: null,
                panelType: null,
                searchArray: [],
            },
        };
    },
    beforeDestroy() {
        Object.values(this.copyTimers).forEach(timer => clearTimeout(timer));
    },
    methods: {
        ...panelMethods,
        ...lookupMethods,
        getPanelList() {
            return this.getAllPanel({filter: this.searchCriteria});
        },
        searchListing() {
            this.searchCriteria.searchArray = [];
            this.searchCriteria.searchArray.push(this.filter);
            if (this.locationFilter?.value == 1)
                delete this.searchCriteria.location;
            else
                this.searchCriteria.location = this.locationFilter?.value;
            if (this.panelTypeFilter?.value == 1)
                delete this.searchCriteria.panelType;
            else
                this.searchCriteria.panelType = this.panelTypeFilter?.value;
            this.getPanelList();
        },
        async copyPhone(item) {
            const phone = `${item.phoneCode || ''} ${item.phone}`.trim();
            try {
                if (navigator.clipboard) {
                    await navigator.clipboard.writeText(phone);
                } else {
                    const input = document.createElement('textarea');
                    input.value = phone;
                    input.style.position = 'fixed';
                    input.style.opacity = '0';
                    document.body.appendChild(input);
                    input.select();
                    const copied = document.execCommand('copy');
                    input.remove();
                    if (!copied) throw new Error('Unable to copy phone number.');
                }
                this.$set(this.copiedPhones, item.uuid, true);
                clearTimeout(this.copyTimers[item.uuid]);
                this.copyTimers[item.uuid] = setTimeout(() => {
                    this.$delete(this.copiedPhones, item.uuid);
                    delete this.copyTimers[item.uuid];
                }, 2000);
            } catch (error) {
                alertHelper.alertError({message: 'Unable to copy phone number. Please copy it manually.'});
            }
        },
        openForm(item) {
            this.panelLoaded = false;
            this.getPanel({uuid: item.uuid})
                .then((result) => {
                    this.panelLoaded = !!result?.success;
                });
            this.$bvModal.show('panel-details');
        },
    },
    computed: {
        ...mapState(PANEL_MODULE, ['status']),
        tableRows() {
            return this.$store.state.public.panel.list.data || [];
        },
        totalPanels() {
            return this.tableRows.length;
        },
        data() {
            return this.$store.state.public.panel.details;
        },
        locationDdl() {
            return [
                { value: 1, text: i18n.t('label.all') },
                ...this.$store.state.public.lookup.locationDdl,
            ];
        },
        panelTypeDdl() {
            return [
                { value: 1, text: i18n.t('label.all') },
                ...this.$store.state.public.lookup.panelTypeDdl,
            ];
        },
    },
}
</script>

<style scoped>
    .bg-info, .btn-info {
        background-color: #1AB0E6 !important;
    }
</style>
